import { useState, useEffect } from 'react';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CubeIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

interface AnalyticsData {
  totalRevenue: number;
  totalOrders: number;
  totalProducts: number;
  totalCollections: number;
  revenueChange: number;
  ordersChange: number;
  ordersByStatus: {
    pending: number;
    confirmed: number;
    shipped: number;
    delivered: number;
    cancelled: number;
  };
  recentOrders: Array<{
    _id: string;
    orderNumber: string;
    total: number;
    status: string;
    createdAt: string;
  }>;
  topProducts: Array<{
    _id: string;
    name: string;
    brand: string;
    totalSold: number;
    revenue: number;
  }>;
  salesByCategory: Array<{
    category: string;
    count: number;
    revenue: number;
  }>;
  dailySales: Array<{
    date: string;
    orders: number;
    revenue: number;
  }>;
}

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await fetch('/api/orders/stats/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const ordersResponse = await fetch('/api/orders?limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const ordersData = await ordersResponse.json();

      const productsResponse = await fetch('/api/products/admin/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const productsData = await productsResponse.json();

      const collectionsResponse = await fetch('/api/collections/admin/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const collectionsData = await collectionsResponse.json();

      const orders = ordersData.success ? ordersData.data : [];
      const products = productsData.success ? productsData.data.products : [];
      const collections = collectionsData.success ? collectionsData.data : [];

      const totalRevenue = orders.reduce((sum: number, order: any) => 
        order.status !== 'cancelled' ? sum + order.total : sum, 0);
      
      const ordersByStatus = {
        pending: orders.filter((o: any) => o.status === 'pending').length,
        confirmed: orders.filter((o: any) => o.status === 'confirmed').length,
        shipped: orders.filter((o: any) => o.status === 'shipped').length,
        delivered: orders.filter((o: any) => o.status === 'delivered').length,
        cancelled: orders.filter((o: any) => o.status === 'cancelled').length,
      };

      const recentOrders = orders.slice(0, 5).map((order: any) => ({
        _id: order._id,
        orderNumber: order.orderNumber,
        total: order.total,
        status: order.status,
        createdAt: order.createdAt,
      }));

      const productSales: { [key: string]: { name: string; brand: string; totalSold: number; revenue: number } } = {};
      orders.forEach((order: any) => {
        if (order.status !== 'cancelled') {
          order.items?.forEach((item: any) => {
            const productId = item.product?._id || item.productId;
            if (productId) {
              if (!productSales[productId]) {
                productSales[productId] = {
                  name: item.product?.name || 'Unknown',
                  brand: item.product?.brand || 'Unknown',
                  totalSold: 0,
                  revenue: 0
                };
              }
              productSales[productId].totalSold += item.quantity || 1;
              productSales[productId].revenue += (item.price || 0) * (item.quantity || 1);
            }
          });
        }
      });
      
      const topProducts = Object.entries(productSales)
        .map(([id, data]) => ({ _id: id, ...data }))
        .sort((a, b) => b.totalSold - a.totalSold)
        .slice(0, 5);

      const categoryMap: { [key: string]: { count: number; revenue: number } } = {};
      products.forEach((product: any) => {
        const category = product.category || 'other';
        if (!categoryMap[category]) {
          categoryMap[category] = { count: 0, revenue: 0 };
        }
        categoryMap[category].count += 1;
      });

      const salesByCategory = Object.entries(categoryMap).map(([category, data]) => ({
        category,
        count: data.count,
        revenue: data.revenue
      }));

      const dailySales: { [key: string]: { orders: number; revenue: number } } = {};
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        dailySales[dateStr] = { orders: 0, revenue: 0 };
      }

      orders.forEach((order: any) => {
        const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
        if (dailySales[orderDate] && order.status !== 'cancelled') {
          dailySales[orderDate].orders += 1;
          dailySales[orderDate].revenue += order.total;
        }
      });

      const dailySalesArray = Object.entries(dailySales).map(([date, data]) => ({
        date,
        orders: data.orders,
        revenue: data.revenue
      }));

      setAnalytics({
        totalRevenue,
        totalOrders: orders.length,
        totalProducts: products.length,
        totalCollections: collections.length,
        revenueChange: 0,
        ordersChange: 0,
        ordersByStatus,
        recentOrders,
        topProducts,
        salesByCategory,
        dailySales: dailySalesArray,
      });
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} DA`;
  };

  if (loading) {
    return (
      <AdminLayout title="Analytics">
        <div className="flex items-center justify-center py-32"><div className="spinner-lg" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analytics" subtitle="Store performance overview">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Revenue', value: formatCurrency(analytics?.totalRevenue || 0), icon: CurrencyDollarIcon, color: 'bg-green-50 text-green-600' },
          { label: 'Orders', value: analytics?.totalOrders || 0, icon: ShoppingCartIcon, color: 'bg-blue-50 text-blue-600' },
          { label: 'Products', value: analytics?.totalProducts || 0, icon: CubeIcon, color: 'bg-purple-50 text-purple-600' },
          { label: 'Collections', value: analytics?.totalCollections || 0, icon: RectangleStackIcon, color: 'bg-amber-50 text-amber-600' },
        ].map((card) => (
          <div key={card.label} className="bg-white border border-warmgray-200 p-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center ${card.color}`}>
                <card.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[0.65rem] tracking-wider uppercase text-warmgray-500">{card.label}</p>
                <p className="font-display text-lg text-offblack">{card.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Orders by Status */}
        <div className="bg-white border border-warmgray-200 p-6">
          <h3 className="font-display text-lg text-offblack mb-5">Orders by Status</h3>
          <div className="space-y-3">
            {analytics?.ordersByStatus && Object.entries(analytics.ordersByStatus).map(([status, count]) => {
              const colors: Record<string, string> = {
                pending: 'bg-yellow-500', confirmed: 'bg-blue-500', shipped: 'bg-purple-500', delivered: 'bg-green-500', cancelled: 'bg-red-500',
              };
              const total = Object.values(analytics.ordersByStatus).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={status} className="flex items-center">
                  <span className="w-20 text-xs text-warmgray-500 capitalize">{status}</span>
                  <div className="flex-1 mx-3 h-2 bg-warmgray-100 overflow-hidden">
                    <div className={`h-full ${colors[status]} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-sm font-medium text-offblack">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Daily Sales */}
        <div className="bg-white border border-warmgray-200 p-6">
          <h3 className="font-display text-lg text-offblack mb-5">Daily Sales (7 Days)</h3>
          <div className="space-y-3">
            {analytics?.dailySales.map((day) => {
              const maxRev = Math.max(...(analytics?.dailySales.map(d => d.revenue) || [1]));
              const pct = maxRev > 0 ? (day.revenue / maxRev) * 100 : 0;
              const dayName = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' });
              return (
                <div key={day.date} className="flex items-center">
                  <span className="w-10 text-xs text-warmgray-500">{dayName}</span>
                  <div className="flex-1 mx-3 h-4 bg-warmgray-100 overflow-hidden">
                    <div className="h-full bg-offblack transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-28 text-right">
                    <span className="text-xs font-medium text-offblack">{formatCurrency(day.revenue)}</span>
                    <span className="text-[0.6rem] text-warmgray-400 ml-1">({day.orders})</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Products */}
        <div className="bg-white border border-warmgray-200 p-6">
          <h3 className="font-display text-lg text-offblack mb-5">Top Products</h3>
          {analytics?.topProducts && analytics.topProducts.length > 0 ? (
            <div className="space-y-3">
              {analytics.topProducts.map((product, i) => (
                <div key={product._id} className="flex items-center gap-3 p-3 bg-warmgray-50">
                  <span className="w-7 h-7 bg-offblack text-offwhite flex items-center justify-center text-xs font-medium">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-offblack truncate">{product.name}</p>
                    <p className="text-xs text-warmgray-500">{product.brand}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-offblack">{product.totalSold} sold</p>
                    <p className="text-xs text-green-600">{formatCurrency(product.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-warmgray-500 text-center py-8">No sales data yet</p>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-white border border-warmgray-200 p-6">
          <h3 className="font-display text-lg text-offblack mb-5">Recent Orders</h3>
          {analytics?.recentOrders && analytics.recentOrders.length > 0 ? (
            <div className="space-y-3">
              {analytics.recentOrders.map((order) => {
                const statusColors: Record<string, string> = {
                  pending: 'bg-yellow-50 text-yellow-700', confirmed: 'bg-blue-50 text-blue-700',
                  shipped: 'bg-purple-50 text-purple-700', delivered: 'bg-green-50 text-green-700',
                  cancelled: 'bg-red-50 text-red-700',
                };
                return (
                  <div key={order._id} className="flex items-center justify-between p-3 bg-warmgray-50">
                    <div>
                      <p className="text-sm font-medium text-offblack">#{order.orderNumber}</p>
                      <p className="text-xs text-warmgray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[0.65rem] tracking-wide uppercase px-2 py-0.5 ${statusColors[order.status] || 'bg-warmgray-100 text-warmgray-600'}`}>
                        {order.status}
                      </span>
                      <span className="text-sm font-medium text-offblack">{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-warmgray-500 text-center py-8">No orders yet</p>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white border border-warmgray-200 p-6">
        <h3 className="font-display text-lg text-offblack mb-5">Products by Category</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {analytics?.salesByCategory.map((cat) => (
            <div key={cat.category} className="text-center p-4 bg-warmgray-50">
              <p className="font-display text-xl text-offblack">{cat.count}</p>
              <p className="text-xs text-warmgray-500 capitalize mt-1">{cat.category}</p>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
